import React, { useCallback } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { TextField } from "components/TextField";
import { styled } from "@mui/material/styles";

const validationSchema = Yup.object({
  name: Yup.string()
    .label("Name")
    .required(),
  entitlements: Yup.array().label("Entitlements")
});

export const NewRoleDialog = ({
  open,
  loading,
  entitlements,
  onClose,
  onSubmit
}) => {
  const formik = useFormik({
    initialValues: {
      name: "",
      entitlements: []
    },
    validationSchema,
    onSubmit: values => {
      onSubmit(values);
    }
  });

  const handleClose = useCallback(() => {
    if (!loading) {
      formik.resetForm();
      onClose();
    }
  }, [formik, loading, onClose]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      onClose={handleClose}
      aria-labelledby="New-Role-Dialog"
      data-qa="Settings-Add-New-Role-Dialog"
    >
      <DialogTitle>Create a new role</DialogTitle>
      <DialogContent>
        <TextField
          name="name"
          label="Role name"
          inputProps={{ "aria-label": "role name input" }}
          size="small"
          fullWidth
          required
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          disabled={loading}
        />

        <DividerWithMargin />

        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel>Entitlements</InputLabel>
          <Select
            name="entitlements"
            label="Entitlements"
            variant="outlined"
            size="small"
            required
            multiple
            fullWidth
            value={formik.values.entitlements}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.entitlements && Boolean(formik.errors.entitlements)
            }
            disabled={loading}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left"
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left"
              }
            }}
          >
            {entitlements?.map(entitlement => (
              <MenuItem key={entitlement.id} value={entitlement.id}>
                {entitlement.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          data-qa="Settings-Button-Cancel-Create-Role"
          variant="outlined"
          color={"primary"}
          disabled={loading}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          data-qa="Settings-Button-Create-Role"
          variant="contained"
          color="primary"
          disabled={loading || formik.values.name.length === 0}
          onClick={() => formik.handleSubmit()}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DividerWithMargin = styled(Divider)`
  margin: ${props => props.theme.spacing(1)} 0;
`;

NewRoleDialog.propTypes = {
  open: PropTypes.bool,
  loading: PropTypes.bool,
  entitlements: PropTypes.array,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};
